import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';

const RagHybridFlowDiagram = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const stagesCount = 5; // Nombre total d'étapes

  // Navigation handler for React components
  const navigateToPage = (e, pageName) => {
    e.preventDefault();
    if (window.onNavigateToPage) {
      window.onNavigateToPage(pageName);
    } else {
      console.log(`Navigation vers: ${pageName}`);
    }
  };

  // Animation du flux toutes les 2 secondes
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % stagesCount);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Fonction pour déterminer l'opacité en fonction de l'étape actuelle
  const getStageOpacity = (stageIndex) => {
    return stageIndex <= currentStep ? '1' : '0.3';
  };

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            margin: 0;
            padding: 20px;
            background: #0a0a0a;
            color: #ffffff;
        }
        
        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.95);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            z-index: 1000;
            padding: 1rem 2rem;
        }
        
        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .back-link {
            color: #667eea;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: gap 0.3s;
        }
        
        .back-link:hover {
            gap: 1rem;
        }
        
        .container {
            max-width: 1200px;
            margin: 0 auto;
            background: rgba(255, 255, 255, 0.03);
            padding: 30px;
            border-radius: 20px;
            border: 1px solid rgba(255, 255, 255, 0.1);
            box-shadow: 0 2px 10px rgba(0,0,0,0.3);
            margin-top: 100px;
        }
        
        h1 {
            text-align: center;
            font-size: 3rem;
            margin-bottom: 2rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .page-subtitle {
            text-align: center;
            font-size: 1.2rem;
            color: #888;
            margin-bottom: 40px;
        }
        
        .flow-diagram {
            position: relative;
            min-height: 800px;
        }
        
        .stage {
            background: rgba(255, 255, 255, 0.03);
            border: 2px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 20px;
            margin: 20px 0;
            position: relative;
            transition: all 0.3s ease;
        }
        
        .stage:hover {
            border-color: rgba(102, 126, 234, 0.5);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
            background: rgba(255, 255, 255, 0.05);
        }
        
        .stage-title {
            font-size: 18px;
            font-weight: bold;
            color: #ffffff;
            margin-bottom: 10px;
            display: flex;
            align-items: center;
            gap: 10px;
        }
        
        .stage-icon {
            width: 30px;
            height: 30px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 16px;
        }
        
        .parallel-container {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 20px;
            margin: 20px 0;
        }
        
        .method-box {
            background: rgba(255, 255, 255, 0.05);
            border: 2px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            padding: 15px;
            text-align: center;
            transition: all 0.3s ease;
        }
        
        .method-box:hover {
            background: rgba(102, 126, 234, 0.1);
            border-color: rgba(102, 126, 234, 0.3);
            transform: translateY(-2px);
        }
        
        .method-title {
            font-weight: bold;
            color: #ffffff;
            margin-bottom: 8px;
        }
        
        .method-weight {
            font-size: 24px;
            font-weight: bold;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            margin: 10px 0;
        }
        
        .method-details {
            font-size: 12px;
            color: #888;
        }
        
        .arrow {
            width: 2px;
            height: 40px;
            background: rgba(255, 255, 255, 0.3);
            margin: 0 auto;
            position: relative;
        }
        
        .arrow::after {
            content: '▼';
            position: absolute;
            bottom: -10px;
            left: 50%;
            transform: translateX(-50%);
            color: #667eea;
            font-size: 20px;
        }
        
        .fusion-box {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 20px;
            border-radius: 16px;
            text-align: center;
            margin: 20px auto;
            max-width: 600px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
        }
        
        .results-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 15px;
            margin-top: 20px;
        }
        
        .result-card {
            background: rgba(255, 255, 255, 0.05);
            border-left: 4px solid #67e8a9;
            border-radius: 8px;
            padding: 15px;
            border: 1px solid rgba(255, 255, 255, 0.1);
            transition: all 0.3s ease;
        }
        
        .result-card:hover {
            background: rgba(255, 255, 255, 0.08);
            transform: translateY(-2px);
        }
        
        .result-score {
            font-size: 20px;
            font-weight: bold;
            color: #67e8a9;
        }
        
        .timeline {
            display: flex;
            justify-content: space-between;
            margin-top: 40px;
            padding: 20px;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
        }
        
        .time-point {
            text-align: center;
        }
        
        .time-value {
            font-size: 24px;
            font-weight: bold;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .time-label {
            font-size: 12px;
            color: #888;
            margin-top: 5px;
        }
        
        @keyframes pulse {
            0% {
                transform: scale(1);
            }
            50% {
                transform: scale(1.05);
            }
            100% {
                transform: scale(1);
            }
        }
        
        .active {
            animation: pulse 2s infinite;
        }
        
        .stats-box {
            background: rgba(67, 232, 169, 0.1);
            border: 1px solid rgba(67, 232, 169, 0.3);
            padding: 15px;
            border-radius: 16px;
            margin-top: 20px;
        }
        
        .stat-item {
            display: flex;
            justify-content: space-between;
            margin: 5px 0;
            color: #ccc;
        }
        
        .stat-item strong {
            color: #67e8a9;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
            .parallel-container {
                grid-template-columns: 1fr;
            }
            
            .timeline {
                flex-direction: column;
                gap: 1rem;
            }
            
            .results-grid {
                grid-template-columns: 1fr;
            }
        }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="#" onClick={(e) => navigateToPage(e, 'index-page')} className="back-link">
            <ChevronLeft size={16} /> Retour à l'accueil
          </a>
          <div>Flux RAG Hybride</div>
        </div>
      </nav>

      <div className="container">
        <h1>🚀 Architecture RAG Hybride - Flux de Recherche</h1>
        <p className="page-subtitle">Visualisation interactive du processus de recherche hybride</p>
        
        <div className="flow-diagram">
          {/* Étape 1: Analyse de requête */}
          <div className="stage" style={{ opacity: getStageOpacity(0) }}>
            <div className="stage-title">
              <div className="stage-icon">1</div>
              Analyse de la Requête
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <strong style={{color: '#67e8a9'}}>Requête:</strong> "Comment traiter un chèque impayé selon BEAC?"
              </div>
              <div>
                <strong style={{color: '#67e8a9'}}>Analyse:</strong>
                <ul style={{ margin: '5px 0', paddingLeft: '20px', color: '#ccc' }}>
                  <li>Intent: Compliance + Operational</li>
                  <li>Entités: [chèque, BEAC]</li>
                  <li>Keywords: [traiter, impayé, règles]</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="arrow"></div>
          
          {/* Étape 2: Recherche parallèle */}
          <div className="stage" style={{ opacity: getStageOpacity(1) }}>
            <div className="stage-title">
              <div className="stage-icon">2</div>
              Recherche Multi-Méthodes Parallèle
            </div>
            
            <div className="parallel-container">
              <div className="method-box">
                <div className="method-title">🧠 Vectorielle</div>
                <div className="method-weight">60%</div>
                <div className="method-details">
                  Embedding sémantique<br />
                  FAISS/Qdrant<br />
                  Top 20 résultats
                </div>
              </div>
              
              <div className="method-box">
                <div className="method-title">🔍 Keywords</div>
                <div className="method-weight">25%</div>
                <div className="method-details">
                  BM25 Ranking<br />
                  Exact match<br />
                  Top 15 résultats
                </div>
              </div>
              
              <div className="method-box">
                <div className="method-title">🕸️ Graphe</div>
                <div className="method-weight">10%</div>
                <div className="method-details">
                  Relations Neo4j<br />
                  Entités liées<br />
                  Top 10 résultats
                </div>
              </div>
              
              <div className="method-box">
                <div className="method-title">📋 Metadata</div>
                <div className="method-weight">5%</div>
                <div className="method-details">
                  Filtres structurés<br />
                  Authority: BEAC<br />
                  Type: Procedure
                </div>
              </div>
            </div>
          </div>
          
          <div className="arrow"></div>
          
          {/* Étape 3: Fusion */}
          <div 
            className="fusion-box active" 
            style={{ opacity: getStageOpacity(2) }}
          >
            <h3 style={{ marginTop: 0 }}>⚡ Fusion des Résultats</h3>
            <p>Algorithme de fusion RRF (Reciprocal Rank Fusion)</p>
            <p>Score_final = Σ(weight × method_score) + diversity_bonus</p>
          </div>
          
          <div className="arrow"></div>
          
          {/* Étape 4: Re-ranking */}
          <div className="stage" style={{ opacity: getStageOpacity(3) }}>
            <div className="stage-title">
              <div className="stage-icon">3</div>
              Re-ranking avec Cross-Encoder
            </div>
            <div className="results-grid">
              <div className="result-card">
                <div className="result-score">95.2%</div>
                <strong style={{color: '#fff'}}>Procédure BEAC n°2023-15</strong>
                <p style={{ fontSize: '14px', margin: '5px 0', color: '#ccc' }}>
                  Traitement des chèques impayés - Article 4.2
                </p>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  Methods: Vector + Keyword + Graph
                </div>
              </div>
              
              <div className="result-card">
                <div className="result-score">89.7%</div>
                <strong style={{color: '#fff'}}>Module: gub_cheque_reject.4gl</strong>
                <p style={{ fontSize: '14px', margin: '5px 0', color: '#ccc' }}>
                  Fonction de rejet automatique
                </p>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  Methods: Vector + Keyword
                </div>
              </div>
              
              <div className="result-card">
                <div className="result-score">84.3%</div>
                <strong style={{color: '#fff'}}>Guide Utilisateur - Section 7</strong>
                <p style={{ fontSize: '14px', margin: '5px 0', color: '#ccc' }}>
                  Procédure manuelle de traitement
                </p>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  Methods: Vector + Metadata
                </div>
              </div>
            </div>
          </div>
          
          <div className="arrow"></div>
          
          {/* Étape 5: Résultat final */}
          <div 
            className="stage" 
            style={{ 
              background: 'rgba(67, 232, 169, 0.1)',
              border: '1px solid rgba(67, 232, 169, 0.3)',
              opacity: getStageOpacity(4)
            }}
          >
            <div className="stage-title">
              <div className="stage-icon">✓</div>
              Réponse Générée avec Contexte Enrichi
            </div>
            <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '15px', borderRadius: '8px', marginTop: '10px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <p><strong style={{color: '#67e8a9'}}>Réponse:</strong></p>
              <p style={{color: '#ccc'}}>Pour traiter un chèque impayé selon les règles BEAC, vous devez suivre la procédure définie dans la directive BEAC n°2023-15, Article 4.2:</p>
              <ol style={{color: '#ccc'}}>
                <li>Vérifier le motif de rejet dans le système (fonction check_reject_reason)</li>
                <li>Appliquer les frais réglementaires selon le barème BEAC</li>
                <li>Notifier le client dans les 48h ouvrées</li>
                <li>Enregistrer l'incident dans le fichier central des incidents de paiement</li>
              </ol>
              <p style={{color: '#888'}}><em>Sources: 3 documents pertinents trouvés avec une confiance de 95.2%</em></p>
            </div>
          </div>
          
          {/* Timeline de performance */}
          <div className="timeline">
            <div className="time-point">
              <div className="time-value">50ms</div>
              <div className="time-label">Analyse</div>
            </div>
            <div className="time-point">
              <div className="time-value">800ms</div>
              <div className="time-label">Recherche</div>
            </div>
            <div className="time-point">
              <div className="time-value">200ms</div>
              <div className="time-label">Fusion</div>
            </div>
            <div className="time-point">
              <div className="time-value">300ms</div>
              <div className="time-label">Re-ranking</div>
            </div>
            <div className="time-point">
              <div className="time-value">650ms</div>
              <div className="time-label">Génération</div>
            </div>
            <div 
              className="time-point" 
              style={{ 
                background: 'rgba(67, 232, 169, 0.1)', 
                padding: '10px', 
                borderRadius: '8px',
                border: '1px solid rgba(67, 232, 169, 0.3)'
              }}
            >
              <div className="time-value">2.0s</div>
              <div className="time-label">TOTAL</div>
            </div>
          </div>
          
          {/* Statistiques */}
          <div className="stats-box">
            <h4 style={{ marginTop: 0, color: '#67e8a9' }}>📊 Statistiques de Recherche</h4>
            <div className="stat-item">
              <span>Documents analysés:</span>
              <strong>1,247</strong>
            </div>
            <div className="stat-item">
              <span>Candidats générés:</span>
              <strong>65</strong>
            </div>
            <div className="stat-item">
              <span>Après fusion:</span>
              <strong>12</strong>
            </div>
            <div className="stat-item">
              <span>Résultats finaux:</span>
              <strong>5</strong>
            </div>
            <div className="stat-item">
              <span>Cache hit:</span>
              <strong>Non (première requête)</strong>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RagHybridFlowDiagram;